import { formatPercent, initials } from '@obras/shared';
import { CircleAlert, RotateCw, type LucideIcon } from 'lucide-react';
import type { ReactNode } from 'react';

import { cn } from '../../lib/cn.js';
import { Button } from './Button.js';

/**
 * Skeleton com a forma real do conteúdo — design.md §8.
 * Nunca substituir conteúdo já renderizado por skeleton: causa piscada.
 */
export function Skeleton({ className }: { className?: string }) {
  return <div className={cn('skeleton', className)} aria-hidden />;
}

export function SkeletonText({ lines = 3 }: { lines?: number }) {
  return (
    <div className="flex flex-col gap-2">
      {Array.from({ length: lines }, (_, index) => (
        <Skeleton key={index} className={cn('h-3.5', index === lines - 1 ? 'w-2/3' : 'w-full')} />
      ))}
    </div>
  );
}

/** Barra fina de revalidação: o dado antigo continua em tela enquanto ela roda. */
export function RefreshingBar({ active }: { active: boolean }) {
  if (!active) return null;

  return (
    <div className="h-0.5 w-full overflow-hidden rounded-full bg-muted" role="status">
      <div className="h-full w-1/3 animate-[indeterminate_1.2s_var(--ease-out-quint)_infinite] bg-primary" />
      <span className="sr-only">Atualizando…</span>
    </div>
  );
}

export function EmptyState({
  icon: Icon,
  title,
  description,
  action,
}: {
  icon: LucideIcon;
  title: string;
  description: string;
  action?: ReactNode | undefined;
}) {
  return (
    <div className="flex flex-col items-center gap-3 px-6 py-16 text-center">
      <span className="grid size-11 place-items-center rounded-lg bg-muted">
        <Icon size={20} strokeWidth={1.5} className="text-muted-foreground" aria-hidden />
      </span>
      <h3 className="text-[15px] font-semibold tracking-tight">{title}</h3>
      <p className="max-w-[46ch] text-[13px] text-muted-foreground">{description}</p>
      {action ? <div className="mt-1">{action}</div> : null}
    </div>
  );
}

export function ErrorState({
  title = 'Não foi possível carregar',
  message,
  onRetry,
}: {
  title?: string | undefined;
  message: string;
  onRetry?: (() => void) | undefined;
}) {
  return (
    <div className="rounded-lg bg-danger-bg p-5" role="alert">
      <div className="flex items-start gap-3">
        <CircleAlert
          size={18}
          strokeWidth={1.75}
          className="mt-0.5 shrink-0 text-danger"
          aria-hidden
        />
        <div className="flex-1">
          <h3 className="text-[15px] font-semibold tracking-tight text-danger">{title}</h3>
          <p className="mt-1 text-[13px] text-foreground/70">{message}</p>
          {onRetry ? (
            <Button
              size="sm"
              className="mt-3 bg-card hover:bg-card/70"
              onClick={onRetry}
              icon={<RotateCw size={14} strokeWidth={1.75} />}
            >
              Tentar novamente
            </Button>
          ) : null}
        </div>
      </div>
    </div>
  );
}

/** Erro de formulário que não pertence a um campo específico. */
export function FormError({ children }: { children: ReactNode }) {
  if (!children) return null;

  return (
    <div
      role="alert"
      className="flex items-start gap-2.5 rounded-md bg-danger-bg px-3.5 py-3 text-[13px] text-danger"
    >
      <CircleAlert size={15} strokeWidth={1.75} className="mt-px shrink-0" aria-hidden />
      <span className="min-w-0 flex-1">{children}</span>
    </div>
  );
}

/** Aviso em faixa — informação de contexto que não é erro. */
export function Notice({
  tone = 'muted',
  icon: Icon,
  children,
}: {
  tone?: 'muted' | 'primary' | 'warning' | undefined;
  icon?: LucideIcon | undefined;
  children: ReactNode;
}) {
  const tones = {
    muted: 'bg-muted text-muted-foreground',
    primary: 'bg-primary-bg text-primary-strong',
    warning: 'bg-warning-bg text-warning',
  }[tone];

  return (
    <div className={cn('flex items-start gap-2.5 rounded-md px-3.5 py-3 text-[13px]', tones)}>
      {Icon ? <Icon size={15} strokeWidth={1.75} className="mt-px shrink-0" aria-hidden /> : null}
      <div className="min-w-0 flex-1">{children}</div>
    </div>
  );
}

export function Avatar({
  name,
  url,
  size = 28,
}: {
  name: string;
  url?: string | null | undefined;
  size?: number | undefined;
}) {
  if (url) {
    return (
      <img
        src={url}
        alt=""
        width={size}
        height={size}
        className="shrink-0 rounded-full object-cover"
        style={{ width: size, height: size }}
      />
    );
  }

  return (
    <span
      aria-hidden
      className="grid shrink-0 place-items-center rounded-full bg-accent font-medium text-foreground"
      style={{ width: size, height: size, fontSize: Math.round(size * 0.38) }}
    >
      {initials(name)}
    </span>
  );
}

/**
 * Barra de progresso. O percentual aparece sempre em texto ao lado:
 * a barra sozinha não comunica — design.md §7.
 *
 * O tom padrão é neutro: prazo correndo não é destaque. Laranja e vermelho
 * ficam reservados ao que exige ação.
 */
export function ProgressBar({
  value,
  label,
  tone = 'neutral',
}: {
  value: number;
  label?: string | undefined;
  tone?: 'neutral' | 'primary' | 'success' | 'danger' | undefined;
}) {
  const clamped = Math.min(Math.max(value, 0), 1);

  const fill = {
    neutral: 'bg-foreground/75',
    primary: 'bg-primary',
    success: 'bg-success',
    danger: 'bg-danger',
  }[tone];

  return (
    <div className="flex items-center gap-3">
      <div
        className="h-1.5 flex-1 overflow-hidden rounded-full bg-muted"
        role="progressbar"
        aria-valuenow={Math.round(clamped * 100)}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-label={label ?? 'Progresso'}
      >
        <div
          className={cn('h-full rounded-full transition-[width] duration-500 ease-out', fill)}
          style={{ width: `${clamped * 100}%` }}
        />
      </div>
      <span className="tabular w-12 text-right text-[12px] font-medium text-muted-foreground">
        {formatPercent(clamped)}
      </span>
    </div>
  );
}
